import { recordGuestLineupAppearances } from "@/lib/api/guestProfiles";
import { supabase } from "@/lib/supabase";
import { parseISO, startOfWeek } from "date-fns";
import {
  parseEventLineup,
  serializeLineupSide,
  type EventLineup,
  type LineupMember,
} from "@/lib/v2/lineup";
import type { GuestBalanceHint } from "@/lib/v2/guestBalance";
import { buildTentativeLineup } from "@/lib/v2/tentativeLineup";
import { validateMatch } from "@/lib/validators";
import {
  computeMinPlayers,
  countGoing,
  countReserves,
  canAddReserve,
  isMatchConfirmed,
  handleGoingDrop,
  applyReservePromotion,
  resolveJoinStatus,
  type RsvpLike,
} from "@/lib/v2/confirmation";

export type RsvpStatus = "going" | "maybe" | "out" | "reserve";

export interface MatchEvent {
  id: string;
  group_id: string;
  event_date: string;
  location: string | null;
  max_players: number | null;
  status: "draft" | "convocatoria" | "completed" | "cancelled";
  created_by: string | null;
  poll_option_id?: string | null;
  min_players?: number | null;
  lineup?: EventLineup | null;
  match_id?: number | null;
  kickoff_at?: string | null;
  rsvp_public_token?: string | null;
  rsvp_password_hash?: string | null;
}

export interface Rsvp {
  id: string;
  event_id: string;
  player_id: number | null;
  guest_name: string | null;
  guest_balance_hint?: GuestBalanceHint | null;
  status: RsvpStatus;
  token: string;
  added_by_user_id?: string | null;
}

export interface PlayerRecord {
  id: number;
  name: string;
}

export interface MatchRecord {
  id: number;
  match_date: string;
  teama: number[];
  teamb: number[];
  scorea: number | null;
  scoreb: number | null;
  winner: string | null;
  group_id: string | null;
}

export interface CompletedMatchContext {
  event: MatchEvent;
  match: MatchRecord | null;
  /** Past kickoff ≥48h with no linked match row. */
  missingResult?: boolean;
}

const RESULT_OVERDUE_MS = 48 * 60 * 60 * 1000;

function isEventInCurrentWeek(eventDateIso: string): boolean {
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  return parseISO(eventDateIso).getTime() >= weekStart.getTime();
}

/** Active week event: not cancelled or completed (includes past kickoff awaiting result). */
export async function fetchCurrentMatchEvent(groupId: string): Promise<MatchEvent | null> {
  const { data, error } = await supabase
    .from("match_events")
    .select("*")
    .eq("group_id", groupId)
    .in("status", ["draft", "convocatoria"])
    .order("event_date", { ascending: false });

  if (error) throw error;
  if (!data?.length) return null;

  const now = Date.now();
  const inCurrentWeek = (event: MatchEvent) => isEventInCurrentWeek(event.event_date);

  const futureThisWeek = [...data]
    .filter((event) => inCurrentWeek(event) && parseISO(event.event_date).getTime() > now)
    .sort(
      (a, b) => parseISO(a.event_date).getTime() - parseISO(b.event_date).getTime()
    );
  if (futureThisWeek[0]) return futureThisWeek[0];

  const pastThisWeek = [...data]
    .filter((event) => inCurrentWeek(event) && parseISO(event.event_date).getTime() <= now)
    .sort(
      (a, b) => parseISO(b.event_date).getTime() - parseISO(a.event_date).getTime()
    );
  if (pastThisWeek[0]) return pastThisWeek[0];

  return null;
}

/** Most recent completed event with its linked match row when available. */
export async function fetchLastCompletedMatch(
  groupId: string
): Promise<CompletedMatchContext | null> {
  const { data: event, error: eventError } = await supabase
    .from("match_events")
    .select("*")
    .eq("group_id", groupId)
    .eq("status", "completed")
    .order("event_date", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (eventError) throw eventError;
  if (!event) return null;

  let match: MatchRecord | null = null;
  if (event.match_id != null) {
    const { data: matchRow, error: matchError } = await supabase
      .from("matches")
      .select("*")
      .eq("id", event.match_id)
      .maybeSingle();
    if (matchError) throw matchError;
    match = matchRow;
  }

  return { event, match };
}

export async function fetchMatchById(matchId: number): Promise<MatchRecord | null> {
  const { data, error } = await supabase
    .from("matches")
    .select("*")
    .eq("id", matchId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/** True when an admin has published both sides of the lineup on the event. */
export function hasPublishedLineup(
  event: Pick<MatchEvent, "lineup">
): boolean {
  const lineup = event.lineup;
  return Boolean(lineup?.teamA?.length && lineup?.teamB?.length);
}

/** Return player IDs from a published lineup (guests excluded — matches table is player-only). */
export function resolveTeamsForMatchEvent(
  event: Pick<MatchEvent, "lineup">,
  _goingPlayerIds: number[] = [],
  context: { playerNameById?: Map<number, string> } = {}
): { teamA: number[]; teamB: number[] } {
  if (!hasPublishedLineup(event)) {
    throw new Error("LINEUP_NOT_PUBLISHED");
  }

  const parsed = parseEventLineup(event.lineup, {
    playerNameById: context.playerNameById ?? new Map(),
  });
  return { teamA: parsed.playerIdsA, teamB: parsed.playerIdsB };
}

/**
 * Resolve teams for logging a match result.
 * Uses the published lineup when available; otherwise auto-balances going players.
 */
export function resolveTeamsForMatchResult(
  event: Pick<MatchEvent, "id" | "lineup">,
  goingPlayerIds: number[],
  context: { playerNameById?: Map<number, string> } = {}
): { teamA: number[]; teamB: number[] } {
  if (hasPublishedLineup(event)) {
    return resolveTeamsForMatchEvent(event, goingPlayerIds, context);
  }

  if (goingPlayerIds.length === 0) {
    throw new Error("NO_PLAYERS_FOR_MATCH");
  }

  return buildTentativeLineup(goingPlayerIds, event.id);
}

export type LogMatchResultInput =
  | { scoreA: number; scoreB: number; winner?: never }
  | { winner: "A" | "B" | "Draw"; scoreA?: never; scoreB?: never };

/**
 * Create or update the matches row for an event, mark the event completed, and link match_id.
 * Re-submitting scores updates the existing match (no hard lock on first submit).
 */
export async function logMatchResultForEvent(
  event: MatchEvent,
  result: LogMatchResultInput,
  groupId: string,
  goingPlayerIds: number[],
  options: {
    playerNameById?: Map<number, string>;
    guestNameByRsvpId?: Map<string, string>;
  } = {}
): Promise<MatchRecord> {
  const scoreA = "scoreA" in result ? result.scoreA : null;
  const scoreB = "scoreB" in result ? result.scoreB : null;
  const winner =
    "winner" in result
      ? result.winner
      : scoreA! > scoreB!
        ? "A"
        : scoreB! > scoreA!
          ? "B"
          : "Draw";
  const { teamA, teamB } = resolveTeamsForMatchResult(event, goingPlayerIds, {
    playerNameById: options.playerNameById,
  });

  const parsed = parseEventLineup(event.lineup, {
    playerNameById: options.playerNameById ?? new Map(),
    guestNameByRsvpId: options.guestNameByRsvpId,
  });

  const guestUpdates = [
    ...parsed.teamA
      .filter((m) => m.guestName)
      .map((m) => ({
        guestName: m.guestName!,
        balanceScore: m.balanceScore,
        won: winner === "A",
      })),
    ...parsed.teamB
      .filter((m) => m.guestName)
      .map((m) => ({
        guestName: m.guestName!,
        balanceScore: m.balanceScore,
        won: winner === "B",
      })),
  ];

  if (guestUpdates.length > 0) {
    await recordGuestLineupAppearances(groupId, guestUpdates);
  }

  const matchPayload = validateMatch({
    match_date: new Date(event.event_date).toISOString(),
    teama: teamA,
    teamb: teamB,
    scorea: scoreA,
    scoreb: scoreB,
    winner,
    group_id: groupId,
    goalscorers: [],
    youtubelink: null,
  });

  let match: MatchRecord;

  if (event.match_id != null) {
    const { data, error } = await supabase
      .from("matches")
      .update({
        scorea: matchPayload.scorea,
        scoreb: matchPayload.scoreb,
        winner: matchPayload.winner,
        teama: matchPayload.teama,
        teamb: matchPayload.teamb,
      })
      .eq("id", event.match_id)
      .select()
      .single();

    if (error) throw error;
    match = data;
  } else {
    const { data, error } = await supabase
      .from("matches")
      .insert(matchPayload)
      .select()
      .single();

    if (error) throw error;
    match = data;

    const { error: linkError } = await supabase
      .from("match_events")
      .update({ status: "completed", match_id: match.id })
      .eq("id", event.id);

    if (linkError) throw linkError;
  }

  if (event.status !== "completed") {
    const { error: statusError } = await supabase
      .from("match_events")
      .update({ status: "completed" })
      .eq("id", event.id);

    if (statusError) throw statusError;
  }

  return match;
}

export async function fetchUpcomingEvents(groupId: string): Promise<MatchEvent[]> {
  const { data, error } = await supabase
    .from("match_events")
    .select("*")
    .eq("group_id", groupId)
    .gte("event_date", new Date().toISOString())
    .neq("status", "cancelled")
    .order("event_date", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

/** Non-completed, non-cancelled events (includes post-kickoff awaiting result). */
export async function fetchActiveEvents(groupId: string): Promise<MatchEvent[]> {
  const { data, error } = await supabase
    .from("match_events")
    .select("*")
    .eq("group_id", groupId)
    .in("status", ["draft", "convocatoria"])
    .order("event_date", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

/** Completed events with linked match rows when available. */
export async function fetchCompletedEventsWithMatches(
  groupId: string
): Promise<CompletedMatchContext[]> {
  const { data: events, error: eventError } = await supabase
    .from("match_events")
    .select("*")
    .eq("group_id", groupId)
    .eq("status", "completed")
    .order("event_date", { ascending: false });

  if (eventError) throw eventError;
  if (!events?.length) return [];

  const matchIds = events
    .map((e) => e.match_id)
    .filter((id): id is number => id != null);

  const matchById = new Map<number, MatchRecord>();
  if (matchIds.length > 0) {
    const { data: matches, error: matchError } = await supabase
      .from("matches")
      .select("*")
      .in("id", matchIds);
    if (matchError) throw matchError;
    for (const row of matches ?? []) {
      matchById.set(row.id, row);
    }
  }

  return events.map((event) => ({
    event,
    match: event.match_id != null ? (matchById.get(event.match_id) ?? null) : null,
  }));
}

/** Events past kickoff ≥48h without a logged result (still on Home as post_kickoff). */
export async function fetchOverdueEventsWithoutResult(
  groupId: string
): Promise<CompletedMatchContext[]> {
  const cutoff = new Date(Date.now() - RESULT_OVERDUE_MS).toISOString();
  const { data: events, error } = await supabase
    .from("match_events")
    .select("*")
    .eq("group_id", groupId)
    .is("match_id", null)
    .lt("event_date", cutoff)
    .in("status", ["draft", "convocatoria", "collecting", "confirmed"])
    .order("event_date", { ascending: false });

  if (error) throw error;
  return (events ?? []).map((event) => ({
    event,
    match: null,
    missingResult: true,
  }));
}

/** Match rows not linked to any group match_event (legacy history). */
export async function fetchUnlinkedMatches(groupId: string): Promise<MatchRecord[]> {
  const { data: linkedRows, error: linkedError } = await supabase
    .from("match_events")
    .select("match_id")
    .eq("group_id", groupId)
    .not("match_id", "is", null);

  if (linkedError) throw linkedError;

  const linkedIds = (linkedRows ?? [])
    .map((r) => r.match_id)
    .filter((id): id is number => id != null);

  let query = supabase
    .from("matches")
    .select("*")
    .eq("group_id", groupId)
    .order("match_date", { ascending: false });

  if (linkedIds.length > 0) {
    query = query.not("id", "in", `(${linkedIds.join(",")})`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

/** Event IDs linked to a match row (must be read before deleting the match). */
export async function fetchEventIdsByMatchId(matchId: number): Promise<string[]> {
  const { data, error } = await supabase
    .from("match_events")
    .select("id")
    .eq("match_id", matchId);

  if (error) throw error;
  return (data ?? []).map((row) => row.id);
}

/** Cancel convocatoria events linked to the given match rows. */
export async function cancelEventsLinkedToMatches(matchIds: number[]): Promise<void> {
  if (matchIds.length === 0) return;

  const { data, error } = await supabase
    .from("match_events")
    .select("id")
    .in("match_id", matchIds);

  if (error) throw error;

  const eventIds = (data ?? []).map((row) => row.id);
  await Promise.all(eventIds.map((id) => cancelMatchEvent(id)));
}

/** Link a match row to an event and mark the event completed. */
export async function linkMatchToEvent(eventId: string, matchId: number): Promise<void> {
  const { error } = await supabase
    .from("match_events")
    .update({ status: "completed", match_id: matchId })
    .eq("id", eventId);

  if (error) throw error;
}

export async function createMatchEvent(
  groupId: string,
  payload: {
    event_date: string;
    location?: string;
    max_players?: number;
  }
): Promise<MatchEvent> {
  const { data: user } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("match_events")
    .insert({
      group_id: groupId,
      event_date: payload.event_date,
      location: payload.location ?? null,
      max_players: payload.max_players ?? null,
      created_by: user.user?.id ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function fetchRsvpsForEvent(eventId: string): Promise<Rsvp[]> {
  const { data, error } = await supabase
    .from("rsvps")
    .select("*")
    .eq("event_id", eventId);

  if (error) throw error;
  return data ?? [];
}

export async function fetchGroupPlayers(groupId: string): Promise<PlayerRecord[]> {
  const { data, error } = await supabase
    .from("players")
    .select("id,name")
    .eq("group_id", groupId)
    .order("name", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function resolveCurrentUserPlayerId(
  groupId: string,
  userId: string,
  fallbackFullName?: string
): Promise<number | null> {
  // Preferred mapping: player metadata stores auth user id.
  const { data: metadataMatch, error: metadataError } = await supabase
    .from("players")
    .select("id")
    .eq("group_id", groupId)
    .contains("metadata", { auth_user_id: userId })
    .limit(1)
    .maybeSingle();

  if (metadataError) throw metadataError;
  if (metadataMatch?.id) return metadataMatch.id;

  // Fallback for seeded/dev data where player name mirrors profile full name.
  if (fallbackFullName?.trim()) {
    const { data: nameMatch, error: nameError } = await supabase
      .from("players")
      .select("id")
      .eq("group_id", groupId)
      .ilike("name", fallbackFullName.trim())
      .limit(1)
      .maybeSingle();

    if (nameError) throw nameError;
    if (nameMatch?.id) return nameMatch.id;
  }

  return null;
}

/** Resolve or create a player row linked to the signed-in user for RSVP flows. */
export async function ensureCurrentUserPlayerId(
  groupId: string,
  userId: string,
  fallbackFullName?: string
): Promise<number | null> {
  const existing = await resolveCurrentUserPlayerId(groupId, userId, fallbackFullName);
  if (existing) return existing;

  const displayName = fallbackFullName?.trim() || "Jugador";
  const { data, error } = await supabase
    .from("players")
    .insert({
      name: displayName,
      skills: 75,
      effort: 75,
      stamina: 75,
      group_id: groupId,
      metadata: { auth_user_id: userId },
    })
    .select("id")
    .single();

  if (error) {
    // Another tab may have created the row; retry lookup once.
    const retry = await resolveCurrentUserPlayerId(groupId, userId, fallbackFullName);
    if (retry) return retry;
    throw error;
  }

  return data.id;
}

export async function upsertRsvp(
  eventId: string,
  status: RsvpStatus,
  options: {
    playerId?: number;
    guestName?: string;
    guestBalanceHint?: GuestBalanceHint;
    token?: string;
    addedByUserId?: string;
  }
): Promise<Rsvp> {
  const row = {
    event_id: eventId,
    player_id: options.playerId ?? null,
    guest_name: options.guestName ?? null,
    guest_balance_hint: options.guestBalanceHint ?? null,
    status,
    added_by_user_id: options.addedByUserId ?? null,
  };

  if (options.token) {
    const { data, error } = await supabase
      .from("rsvps")
      .update({ status })
      .eq("token", options.token)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  if (options.playerId == null && options.guestName?.trim()) {
    const guestName = options.guestName.trim();
    const { data: existingGuest, error: existingGuestError } = await supabase
      .from("rsvps")
      .select("id")
      .eq("event_id", eventId)
      .eq("guest_name", guestName)
      .maybeSingle();

    if (existingGuestError) throw existingGuestError;

    if (existingGuest?.id) {
      const { data, error } = await supabase
        .from("rsvps")
        .update({
          status,
          guest_name: guestName,
          guest_balance_hint: options.guestBalanceHint ?? null,
          added_by_user_id: options.addedByUserId ?? null,
        })
        .eq("id", existingGuest.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    const { data, error } = await supabase
      .from("rsvps")
      .insert({
        event_id: eventId,
        player_id: null,
        guest_name: guestName,
        guest_balance_hint: options.guestBalanceHint ?? "unknown",
        status,
        added_by_user_id: options.addedByUserId ?? null,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from("rsvps")
    .upsert(row, { onConflict: "event_id,player_id" })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function fetchEventById(eventId: string): Promise<MatchEvent | null> {
  const { data, error } = await supabase
    .from("match_events")
    .select("*")
    .eq("id", eventId)
    .single();

  if (error) return null;
  return data;
}

/** Persist the generated lineup JSONB on match_events for the confirmed event. */
export async function saveEventLineup(
  eventId: string,
  teamA: LineupMember[],
  teamB: LineupMember[]
): Promise<void> {
  const lineup: EventLineup = {
    teamA: serializeLineupSide(teamA),
    teamB: serializeLineupSide(teamB),
  };

  const { error } = await supabase
    .from("match_events")
    .update({ lineup })
    .eq("id", eventId);

  if (error) throw error;
}

/** Admin marks the match as started so Home moves to post-kickoff (result entry). */
export async function startPostKickoff(eventId: string): Promise<void> {
  const event = await fetchEventById(eventId);
  if (!event) throw new Error("EVENT_NOT_FOUND");
  if (!hasPublishedLineup(event)) throw new Error("LINEUP_NOT_PUBLISHED");
  if (event.kickoff_at) return;

  const { error } = await supabase
    .from("match_events")
    .update({ kickoff_at: new Date().toISOString() })
    .eq("id", eventId);

  if (error) throw error;
}

export function getConfirmationProgress(
  rsvps: Rsvp[],
  maxPlayers: number,
  minConfirmPct = 70
) {
  const going = countGoing(rsvps);
  const minPlayers = computeMinPlayers(maxPlayers, minConfirmPct);
  return {
    goingCount: going,
    minPlayers,
    maxPlayers,
    confirmed: isMatchConfirmed(going, minPlayers),
    progressPct: maxPlayers > 0 ? Math.min(100, Math.round((going / maxPlayers) * 100)) : 0,
  };
}

/** Persist any status changes between the original and updated RSVP snapshots. */
async function persistRsvpStatusChanges(
  original: Rsvp[],
  updated: RsvpLike[]
): Promise<void> {
  for (const rsvp of updated) {
    const before = original.find((r) => r.id === rsvp.id);
    if (before && before.status !== rsvp.status) {
      const { error } = await supabase
        .from("rsvps")
        .update({ status: rsvp.status })
        .eq("id", rsvp.id);
      if (error) throw error;
    }
  }
}

/** Drop from going and auto-promote the earliest reserve. */
export async function dropFromGoingAndPromoteReserve(
  eventId: string,
  droppedRsvpId: string,
  maxPlayers: number,
  maxReserves = 2
): Promise<Rsvp[]> {
  const rsvps = await fetchRsvpsForEvent(eventId);
  const updated = handleGoingDrop(rsvps as RsvpLike[], droppedRsvpId, maxPlayers, maxReserves);
  await persistRsvpStatusChanges(rsvps, updated);
  return fetchRsvpsForEvent(eventId);
}

/** Promote the earliest reserves into open going slots (without changing other statuses). */
export async function promoteReservesForEvent(
  eventId: string,
  maxPlayers: number,
  maxReserves = 2
): Promise<Rsvp[]> {
  const rsvps = await fetchRsvpsForEvent(eventId);
  const updated = applyReservePromotion(rsvps as RsvpLike[], maxPlayers, maxReserves);
  await persistRsvpStatusChanges(rsvps, updated);
  return fetchRsvpsForEvent(eventId);
}

/**
 * Notify the group when going count newly crosses the quorum threshold.
 * Quorum is derived from RSVPs; match_events stay in convocatoria.
 */
async function maybeDispatchQuorumReached(
  eventId: string,
  wasQuorumReached: boolean,
  isQuorumReached: boolean
): Promise<void> {
  if (!isQuorumReached || wasQuorumReached) return;

  const { dispatchNotification } = await import("./pushNotifications");
  const { data: ev } = await supabase
    .from("match_events")
    .select("group_id")
    .eq("id", eventId)
    .single();
  if (ev?.group_id) {
    await dispatchNotification({
      groupId: ev.group_id,
      type: "quorum_reached",
      eventId,
      title: "Quórum alcanzado",
      body: "El partido está confirmado y la cancha asegurada.",
      url: "/v2/home",
    });
  }
}

export interface RsvpChangeResult {
  appliedStatus: RsvpStatus | null;
  rsvps: Rsvp[];
  quorumReached: boolean;
}

/**
 * Orchestrate a single RSVP change end-to-end:
 * - caps `going` at max_players, overflowing into `reserve` while reserve slots remain
 * - enforces max_reserves when explicitly setting `reserve`
 * - auto-promotes the earliest reserve when a `going` player drops out
 * - fires a quorum notification when going newly crosses the 70% threshold
 *
 * Throws "EVENT_FULL" when going and reserves are both at capacity, and
 * "RESERVES_FULL" when an explicit reserve cannot be added.
 */
export async function applyRsvpChange(
  event: Pick<MatchEvent, "id" | "max_players" | "status">,
  desiredStatus: RsvpStatus,
  actor: {
    playerId?: number;
    guestName?: string;
    guestBalanceHint?: GuestBalanceHint;
    addedByUserId?: string;
  },
  config: { maxReserves?: number; minConfirmPct?: number } = {}
): Promise<RsvpChangeResult> {
  const maxPlayers = event.max_players ?? 0;
  const maxReserves = config.maxReserves ?? 2;
  const minConfirmPct = config.minConfirmPct ?? 70;

  const currentRsvps = await fetchRsvpsForEvent(event.id);
  const existing = currentRsvps.find((r) =>
    actor.playerId != null
      ? r.player_id === actor.playerId
      : actor.guestName != null && r.guest_name === actor.guestName
  );
  const others = currentRsvps.filter((r) => r.id !== existing?.id);

  const minPlayers = computeMinPlayers(maxPlayers, minConfirmPct);
  const wasQuorumReached = isMatchConfirmed(countGoing(currentRsvps), minPlayers);

  let appliedStatus: RsvpStatus = desiredStatus;

  if (desiredStatus === "going") {
    const decision = resolveJoinStatus(
      countGoing(others),
      countReserves(others),
      maxPlayers,
      maxReserves
    );
    if (decision === "full") {
      throw new Error("EVENT_FULL");
    }
    appliedStatus = decision;
  } else if (desiredStatus === "reserve" && !canAddReserve(others, maxReserves)) {
    throw new Error("RESERVES_FULL");
  }

  const wasGoing = existing?.status === "going";

  if (desiredStatus === "out" && wasGoing && existing) {
    // Mark out and auto-promote the earliest reserve into the freed slot.
    await dropFromGoingAndPromoteReserve(event.id, existing.id, maxPlayers, maxReserves);
  } else {
    await upsertRsvp(event.id, appliedStatus, actor);
    // A going slot may have opened (e.g. going -> maybe); promote a reserve.
    if (wasGoing && appliedStatus !== "going") {
      await promoteReservesForEvent(event.id, maxPlayers, maxReserves);
    }
  }

  const rsvps = await fetchRsvpsForEvent(event.id);
  const quorumReached = isMatchConfirmed(countGoing(rsvps), minPlayers);
  await maybeDispatchQuorumReached(event.id, wasQuorumReached, quorumReached);

  return { appliedStatus, rsvps, quorumReached };
}

/**
 * Remove the actor's RSVP entirely (toggle off Voy / No voy / Quizás).
 * Promotes a reserve when a going player clears their response.
 */
export async function clearRsvp(
  event: Pick<MatchEvent, "id" | "max_players" | "status">,
  actor: {
    playerId?: number;
    guestName?: string;
  },
  config: { maxReserves?: number; minConfirmPct?: number } = {}
): Promise<RsvpChangeResult> {
  const maxPlayers = event.max_players ?? 0;
  const maxReserves = config.maxReserves ?? 2;
  const minConfirmPct = config.minConfirmPct ?? 70;
  const minPlayers = computeMinPlayers(maxPlayers, minConfirmPct);

  const currentRsvps = await fetchRsvpsForEvent(event.id);
  const existing = currentRsvps.find((r) =>
    actor.playerId != null
      ? r.player_id === actor.playerId
      : actor.guestName != null && r.guest_name === actor.guestName
  );

  if (!existing) {
    return {
      appliedStatus: null,
      rsvps: currentRsvps,
      quorumReached: isMatchConfirmed(countGoing(currentRsvps), minPlayers),
    };
  }

  const wasQuorumReached = isMatchConfirmed(countGoing(currentRsvps), minPlayers);
  const wasGoing = existing.status === "going";

  const { error } = await supabase.from("rsvps").delete().eq("id", existing.id);
  if (error) throw error;

  if (wasGoing) {
    await promoteReservesForEvent(event.id, maxPlayers, maxReserves);
  }

  const rsvps = await fetchRsvpsForEvent(event.id);
  const quorumReached = isMatchConfirmed(countGoing(rsvps), minPlayers);
  await maybeDispatchQuorumReached(event.id, wasQuorumReached, quorumReached);

  return { appliedStatus: null, rsvps, quorumReached };
}

/** Admin cancels an active convocatoria event. */
export async function cancelMatchEvent(eventId: string): Promise<void> {
  const { error } = await supabase
    .from("match_events")
    .update({ status: "cancelled" })
    .eq("id", eventId);

  if (error) throw error;
}
